/**
 * Cross-file version drift checker.
 *
 * Reads version pins from multiple source files (go.mod, package.json,
 * .env.example, Cargo.toml, docker-compose*.yml) and flags when the same
 * dependency is pinned to different versions across files.
 *
 * Fully deterministic — no AI, no network, pure file parsing.
 */
import { existsSync, readFileSync } from "node:fs";
import { join } from "node:path";
import { globSync } from "glob";
import type { DriftIssue } from "../../types.js";

export interface VersionPin {
  name: string;
  version: string;
  file: string;
}

// ── Parsers ──

function parseGoMod(content: string, file: string): VersionPin[] {
  const pins: VersionPin[] = [];
  for (const line of content.split("\n")) {
    const trimmed = line.trim();
    // Single-line: require github.com/foo/bar v1.2.3
    const single = trimmed.match(/^require\s+(\S+)\s+(v[\d.]+\S*)/);
    if (single) { pins.push({ name: single[1], version: single[2], file }); continue; }
    // Block body line:   github.com/foo/bar v1.2.3
    const block = trimmed.match(/^([\w./\-]+)\s+(v[\d.]+\S*)/);
    // Skip comment lines, standalone "go X.Y" toolchain lines, and "require" keyword itself
    if (block && !block[1].startsWith("//") && block[1] !== "go" && block[1] !== "require" && block[1] !== "module") {
      pins.push({ name: block[1], version: block[2], file });
    }
  }
  return pins;
}

function parseCargoToml(content: string, file: string): VersionPin[] {
  const pins: VersionPin[] = [];
  for (const line of content.split("\n")) {
    // serde = "1.0"  or  serde = { version = "1.0" }
    const simple = line.match(/^([\w-]+)\s*=\s*"([\d.]+\S*)"/);
    if (simple) { pins.push({ name: simple[1], version: simple[2], file }); continue; }
    const table = line.match(/^([\w-]+)\s*=\s*\{[^}]*version\s*=\s*"([\d.]+\S*)"/);
    if (table) pins.push({ name: table[1], version: table[2], file });
  }
  return pins;
}

function parsePackageJson(content: string, file: string): VersionPin[] {
  const pins: VersionPin[] = [];
  try {
    const pkg = JSON.parse(content);
    for (const section of ["dependencies", "devDependencies", "peerDependencies"]) {
      const deps: Record<string, string> = pkg[section] ?? {};
      for (const [name, ver] of Object.entries(deps)) {
        // Skip @types/* packages — they have independent versioning from the actual package
        if (name.startsWith("@types/")) continue;
        // Strip range operators: ^1.2.3 → 1.2.3
        const clean = String(ver).replace(/^[~^>=<]+/, "");
        if (clean) pins.push({ name, version: clean, file });
      }
    }
  } catch { /* invalid JSON */ }
  return pins;
}

function parseEnvFile(content: string, file: string): VersionPin[] {
  const pins: VersionPin[] = [];
  for (const line of content.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;
    // BIFROST_VERSION=v1.4.22  or  FOO_VERSION=1.2.3
    const m = trimmed.match(/^(\w+_VERSION)\s*=\s*v?([\d.]+\S*)/i);
    if (m) {
      // Normalize key: BIFROST_VERSION → bifrost
      const name = m[1].replace(/_VERSION$/i, "").toLowerCase().replace(/_/g, "-");
      pins.push({ name, version: m[2], file });
    }
  }
  return pins;
}

function parseDockerCompose(content: string, file: string): VersionPin[] {
  const pins: VersionPin[] = [];
  for (const line of content.split("\n")) {
    // image: foo/bar:1.2.3  or  image: foo/bar:v1.2.3 — one pass with two captures
    const m = line.match(/image:\s*([\w./\-]+):v?([\d.]+\S*)/);
    if (!m) continue;
    const name = m[1].split("/").pop() ?? m[1];
    pins.push({ name, version: m[2], file });
  }
  return pins;
}

/** Collect all version pins from all detectable source files. */
export function collectVersionPins(projectRoot: string): VersionPin[] {
  const pins: VersionPin[] = [];

  const tryRead = (relPath: string): string | null => {
    const abs = join(projectRoot, relPath);
    if (!existsSync(abs)) return null;
    try { return readFileSync(abs, "utf8"); } catch { return null; }
  };

  const goMod = tryRead("go.mod");
  if (goMod) pins.push(...parseGoMod(goMod, "go.mod"));

  const cargo = tryRead("Cargo.toml");
  if (cargo) pins.push(...parseCargoToml(cargo, "Cargo.toml"));

  const pkgJson = tryRead("package.json");
  if (pkgJson) pins.push(...parsePackageJson(pkgJson, "package.json"));

  for (const envFile of [".env.example", ".env.sample", ".env.template"]) {
    const content = tryRead(envFile);
    if (content) pins.push(...parseEnvFile(content, envFile));
  }

  // Docker Compose files (docker-compose.yml, compose.yaml, etc.)
  const composeFiles = globSync("*compose*.{yml,yaml}", { cwd: projectRoot, maxDepth: 2 });
  for (const f of composeFiles) {
    const content = tryRead(f);
    if (content) pins.push(...parseDockerCompose(content, f));
  }

  return pins;
}

/**
 * Normalize a dependency name for cross-file matching.
 * go.mod has "github.com/foo/bifrost", env has "bifrost" — strip the path prefix.
 */
function normalizeName(name: string): string {
  // github.com/org/repo → repo; golang.org/x/net → net
  if (name.includes("/")) return name.split("/").pop()!.toLowerCase();
  return name.toLowerCase();
}

/** Check for version pins that conflict across files. */
export function checkVersionPins(projectRoot: string): DriftIssue[] {
  const pins = collectVersionPins(projectRoot);
  if (pins.length === 0) return [];

  // Group by normalized name
  const byName = new Map<string, VersionPin[]>();
  for (const pin of pins) {
    const key = normalizeName(pin.name);
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key)!.push(pin);
  }

  const issues: DriftIssue[] = [];

  for (const [key, group] of byName) {
    if (group.length < 2) continue;

    // Collect unique versions (normalize v-prefix for comparison)
    const normalizedVersions = [...new Set(group.map((p) => p.version.startsWith("v") ? p.version.slice(1) : p.version))];
    if (normalizedVersions.length === 1) continue; // all agree — no drift

    // Build a concise description
    const byFile = group.map((p) => `${p.file}@${p.version}`).join(", ");
    issues.push({
      code: "VERSION_PIN_MISMATCH",
      severity: "warning",
      file: group[0].file,
      line: null,
      message: `Version mismatch for "${key}": ${byFile}`,
      suggestion: `Align all version pins for "${key}" to the same value across ${group.map((p) => p.file).join(", ")}.`,
    });
  }

  return issues;
}
